import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react'
import { Navigate, Route, Routes, useNavigate } from 'react-router-dom'
import { Skeleton } from '../../components/ui'
import { useAppDispatch, useAppSelector } from '../../store/hooks'
import { useTenant } from '../../tenant/useTenant'
import { SummaryBar } from './SummaryBar'
import { WizardHeader } from './WizardHeader'
import { useSelectionPersistence } from './useSelectionPersistence'
import { WizardFooterContext, type WizardAction } from './wizardContext'
import { setBranch } from '../../features/booking/selectionSlice'
import { canEnter, firstIncompleteStep, STEP_IDS, stepIndex, type StepId } from './wizardSteps'
import { LocationStep } from './steps/LocationStep'
import { ServicesStep } from './steps/ServicesStep'
import { SpecialistStep } from './steps/SpecialistStep'
import { TimeStep } from './steps/TimeStep'
import { DetailsStep } from './steps/DetailsStep'
import { DoneStep } from './steps/DoneStep'

/**
 * The wizard owns its own chrome: a progress header, a persistent summary bar
 * and one step at a time. Steps never navigate blind — every route passes
 * through <StepFrame>, which sends a customer whose cart cannot support the
 * step back to the first one that still needs them.
 */
export function BookingWizard() {
  const { slug, bootstrap } = useTenant()
  const dispatch = useAppDispatch()
  const ready = useSelectionPersistence(slug)
  const [action, setAction] = useState<WizardAction | null>(null)
  const footerApi = useMemo(() => ({ setAction }), [])
  const selection = useAppSelector((s) => s.selection)

  // A single-branch vendor has no location to choose. Settling the branch here,
  // before any route renders, means step 1 never flashes and the step guards
  // never see a half-built selection.
  const defaultBranchId = bootstrap.default_branch_id
  const autoBranchPending = ready && defaultBranchId !== null && selection.branchId === null

  useEffect(() => {
    if (autoBranchPending && defaultBranchId !== null) dispatch(setBranch(defaultBranchId))
  }, [autoBranchPending, defaultBranchId, dispatch])

  if (!ready || autoBranchPending) {
    return (
      <div className="container-page flex flex-col gap-4 py-12">
        <Skeleton shape="rect" className="h-8 w-48" />
        <Skeleton shape="rect" className="h-64 w-full" />
      </div>
    )
  }

  return (
    <WizardFooterContext.Provider value={footerApi}>
      <Routes>
        <Route index element={<Navigate to={firstIncompleteStep(selection)} replace />} />
        <Route
          path="location"
          element={
            <StepFrame step="location" action={action}>
              <LocationStep />
            </StepFrame>
          }
        />
        <Route
          path="services"
          element={
            <StepFrame step="services" action={action}>
              <ServicesStep />
            </StepFrame>
          }
        />
        <Route
          path="specialist"
          element={
            <StepFrame step="specialist" action={action}>
              <SpecialistStep />
            </StepFrame>
          }
        />
        <Route
          path="time"
          element={
            <StepFrame step="time" action={action}>
              <TimeStep />
            </StepFrame>
          }
        />
        <Route
          path="details"
          element={
            <StepFrame step="details" action={action}>
              <DetailsStep />
            </StepFrame>
          }
        />
        <Route path="done" element={<DoneStep />} />
        <Route path="*" element={<Navigate to={`/${slug}/book`} replace />} />
      </Routes>
    </WizardFooterContext.Provider>
  )
}

interface StepFrameProps {
  step: StepId
  action: WizardAction | null
  children: ReactNode
}

function StepFrame({ step, action, children }: StepFrameProps) {
  const navigate = useNavigate()
  const { slug, bootstrap } = useTenant()
  const selection = useAppSelector((s) => s.selection)

  const goBack = useCallback(() => {
    const previous = STEP_IDS[stepIndex(step) - 1]
    // Step 1 is skipped entirely for single-branch vendors, so "back" from
    // step 2 leaves the wizard rather than bouncing off an invisible picker.
    if (!previous || (previous === 'location' && bootstrap.default_branch_id !== null)) {
      navigate(`/${slug}`)
      return
    }
    navigate(`../${previous}`)
  }, [step, navigate, slug, bootstrap])

  if (!canEnter(step, selection)) {
    return <Navigate to={`../${firstIncompleteStep(selection)}`} replace />
  }

  return (
    <div className="flex min-h-screen flex-col bg-background">
      <WizardHeader step={step} vendorName={bootstrap.vendor.name} onBack={goBack} />
      {/* The summary bar is sticky, so the last row of a step would otherwise
          come to rest underneath it. Clear its full height, not a token gap. */}
      <main className="flex-1 pb-28">{children}</main>
      <SummaryBar
        action={action}
        onEdit={step === 'services' ? undefined : () => navigate('../services')}
      />
    </div>
  )
}
